import AnimatedSection from "../ui/AnimatedSection";
import { about, skills, softSkills, siteConfig } from "../../data/portfolioData";
import { useInView } from "../../hooks/useInView";

function SkillBar({ name, level, delay }) {
  const [ref, isVisible] = useInView();

  return (
    <div ref={ref} className="mb-5">
      <div className="flex justify-between items-center mb-2">
        <span className="font-body text-sm text-ash-200 tracking-wide">{name}</span>
        <span className="font-mono text-xs text-ember-400">{level}%</span>
      </div>
      <div className="h-px w-full bg-carbon-800 relative overflow-hidden">
        <div
          className="absolute top-0 left-0 h-full bg-ember-400 transition-all duration-1000 ease-out"
          style={{
            width: isVisible ? `${level}%` : "0%",
            transitionDelay: `${delay}ms`,
          }}
        />
      </div>
    </div>
  );
}

export default function AboutSection() {
  return (
    <section id="about" className="py-24 lg:py-32 bg-carbon-900">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <AnimatedSection className="mb-16">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-px bg-ember-400" />
            <span className="font-mono text-xs text-ember-400 tracking-widest uppercase">
              About Me
            </span>
          </div>
          <h2 className="font-display text-[clamp(3rem,7vw,6rem)] leading-none tracking-wider text-ash-100">
            WHO I AM
          </h2>
        </AnimatedSection>

        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24">
          {/* Left — bio + stats */}
          <div>
            <AnimatedSection delay={100}>
              {about.bio.split("\n\n").map((para, i) => (
                <p key={i} className="font-body text-ash-400 leading-relaxed text-base lg:text-lg mb-5">
                  {para}
                </p>
              ))}
            </AnimatedSection>

            {/* Stats */}
            <AnimatedSection className="grid grid-cols-2 gap-0 mt-12 border border-carbon-800" delay={200}>
              {about.stats.map((stat, i) => (
                <div
                  key={i}
                  className={`py-8 px-6 ${i % 2 === 0 ? "border-r border-carbon-800" : ""} ${i < 2 ? "border-b border-carbon-800" : ""}`}
                >
                  <div className="font-display text-4xl text-ember-400 mb-1 ember-glow-text">
                    {stat.value}
                  </div>
                  <div className="font-mono text-xs text-ash-600 tracking-widest uppercase">
                    {stat.label}
                  </div>
                </div>
              ))}
            </AnimatedSection>

            {/* Location & availability */}
            <AnimatedSection className="flex items-center gap-6 mt-8" delay={300}>
              <div className="flex items-center gap-2">
                <span className="font-mono text-xs text-ash-600">📍</span>
                <span className="font-mono text-xs text-ash-400 tracking-wide">
                  {siteConfig.location}
                </span>
              </div>
              {siteConfig.availableForWork && (
                <div className="flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                  <span className="font-mono text-xs text-green-400 tracking-wide">
                    Open to opportunities
                  </span>
                </div>
              )}
            </AnimatedSection>
          </div>

          {/* Right — skills */}
          <div>
            <AnimatedSection delay={150}>
              <h3 className="font-heading text-sm tracking-widest uppercase text-ash-400 mb-8">
                Technical Skills
              </h3>
              {skills.map((skill, i) => (
                <SkillBar
                  key={skill.name}
                  name={skill.name}
                  level={skill.level}
                  delay={i * 80}
                />
              ))}
            </AnimatedSection>

            {/* Soft skills */}
            <AnimatedSection className="mt-10" delay={250}>
              <h3 className="font-heading text-sm tracking-widest uppercase text-ash-400 mb-6">
                Approach & Strengths
              </h3>
              <div className="flex flex-wrap gap-3">
                {softSkills.map((skill) => (
                  <span
                    key={skill}
                    className="font-body text-sm text-ash-300 border border-carbon-700 px-4 py-2 hover:border-ember-400/50 hover:text-ember-400 transition-all duration-300"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </AnimatedSection>

            {/* Quote */}
            <AnimatedSection
              className="mt-12 pl-6 border-l-2 border-ember-400"
              delay={350}
            >
              <blockquote className="font-heading text-xl text-ash-300 italic leading-relaxed font-light tracking-wide">
                "Every frame is a decision. Every cut either serves the story or it doesn't."
              </blockquote>
            </AnimatedSection>
          </div>
        </div>
      </div>
    </section>
  );
}
